// Where a horizontally scrolling toolbar is scrolled to, drawn inside the
// toolbar (Gerry, 2026-10-13). A native scrollbar either took height it then
// left blank (macOS hides scrollbars until they are used) or crowded the
// controls; this thumb overlays the bar's bottom edge, adds no height, shows
// only while the strip overflows, and drags like a scrollbar.
import React, { useEffect, useRef, useState } from "react";

interface ScrollPosition {
  /** Thumb offset and length, as percentages of the track. */
  left: number;
  width: number;
  /** 0 at the start, 100 at the end. */
  value: number;
}

/** The scroller's position, or null while it does not overflow. */
export const useHorizontalScrollPosition = (
  scroller: HTMLElement | null
): ScrollPosition | null => {
  const [position, setPosition] = useState<ScrollPosition | null>(null);
  useEffect(() => {
    if (!scroller) return;
    const measure = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scroller;
      if (scrollWidth <= clientWidth + 1) {
        setPosition(null);
        return;
      }
      const range = scrollWidth - clientWidth;
      setPosition({
        left: (scrollLeft / scrollWidth) * 100,
        width: (clientWidth / scrollWidth) * 100,
        value: Math.round((Math.min(scrollLeft, range) / range) * 100),
      });
    };
    measure();
    scroller.addEventListener("scroll", measure, { passive: true });
    // The strip overflows when its pane narrows or its controls change. The
    // children observed are those present now: the menu bar's one strip of
    // controls, which stays mounted while the bar is.
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    for (const child of Array.from(scroller.children)) observer.observe(child);
    return () => {
      scroller.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [scroller]);
  return position;
};

export const ToolbarScrollIndicator: React.FC<{
  scroller: HTMLElement | null;
  scrollerId: string;
  label: string;
}> = ({ scroller, scrollerId, label }) => {
  const position = useHorizontalScrollPosition(scroller);
  const drag = useRef<{
    pointerId: number;
    x: number;
    scrollLeft: number;
  } | null>(null);
  if (!position || !scroller) return null;
  return (
    <div
      role="scrollbar"
      aria-label={label}
      aria-controls={scrollerId}
      aria-orientation="horizontal"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={position.value}
      className="pointer-events-none absolute inset-x-1 -bottom-[3px] h-[3px]"
    >
      <div
        className="pointer-events-auto absolute top-0 h-full cursor-grab touch-none rounded-full bg-[var(--fp-ink-4)] opacity-60 transition-opacity hover:opacity-100 active:cursor-grabbing"
        style={{ left: `${position.left}%`, width: `${position.width}%` }}
        onPointerDown={(event) => {
          if (event.button !== 0 || !event.isPrimary) return;
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            scrollLeft: scroller.scrollLeft,
          };
        }}
        onPointerMove={(event) => {
          const start = drag.current;
          if (start?.pointerId !== event.pointerId) return;
          // The thumb travels the track; the strip travels its full width.
          const track = event.currentTarget.parentElement?.clientWidth ?? 1;
          scroller.scrollLeft =
            start.scrollLeft +
            ((event.clientX - start.x) * scroller.scrollWidth) / track;
        }}
        onPointerUp={(event) => {
          if (drag.current?.pointerId !== event.pointerId) return;
          drag.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onLostPointerCapture={() => {
          drag.current = null;
        }}
      />
    </div>
  );
};
